// Khối "Cổng AI" dưới bước Chia tập và Bản địa hoá (260928 E3-P2 H11).
//
// AI giám khảo đọc kết quả của bước như một người đọc bình thường và chỉ CHẶN lỗi khách quan (có bằng chứng): bịa tình tiết, bỏ sót nguyên tác,
// lộ tên gốc. Mọi quyết định hiển thị (màu · nhãn · nút nào bật) nằm ở `gateView.ts` (hàm thuần, có jest) — file này chỉ vẽ lại.
//  · Sửa lại   = chạy lại BƯỚC (route hiện có; cổng tự sửa 1 lần như H3) — do chỗ gọi truyền vào `onRerunStep`
//  · Chấm lại  = CHỈ chấm lại trạng thái hiện tại (sau khi sửa tay) — nền, không chạy lại bước
//  · Vẫn tiếp tục = vượt cổng có kiểm toán (lý do ≥ 5 ký tự)
import React, { useMemo, useState } from 'react';
import { Alert, Card, Collapse, Input, Modal, Popconfirm, Space, Tag, Tooltip, Typography, message } from 'antd';
import Button from './workbench/WbButton';
import { adaptationApi } from '../../services/api/adaptation';
import type { GateStepKey, ReviewGates } from '../../types/adaptation';
import { describeAdaptationError } from './adaptationUtils';
import { GateColor, GateIssueView, gateSpentUsd, gateView, isOverrideReasonValid, OVERRIDE_REASON_MIN } from './gateView';
import { useFilmJobPoll } from './useFilmJobPoll';
import { chainResultLine, roundRows } from './workbench/chainGateView';
import { useAdoptedJobId } from './workbench/useAdoptedJobId';
import OptionsButton from './workbench/assistant/OptionsButton';

const { Text } = Typography;
const { TextArea } = Input;

interface Props {
  storyId: string;
  stepKey: GateStepKey;
  gates?: ReviewGates | null;
  /** Bước đã có kết quả để chấm (chia tập: có tập; localize: đã chạy). Chưa có và chưa có cổng ⇒ không hiện khối. */
  hasResult: boolean;
  /** "Sửa lại" — chạy lại bước. */
  onRerunStep: () => void;
  /** Chỗ gọi đã mở hộp xác nhận riêng (ví dụ chia tập, nơi copy phụ thuộc chế độ chạy). */
  rerunAlreadyConfirmed?: boolean;
  /** Khoá mọi nút khi chỗ khác đang bận (job của bước đang chạy…). */
  disabled?: boolean;
  /** F36 — job chấm lại cổng BE báo đang chạy: nhận nuôi sau tải lại / đổi chặng. */
  adoptJobId?: string | null;
  /** Override/rejudge xong ⇒ chỗ gọi tải lại outline/view. */
  onChanged: () => void;
  /** 261005 E3-P4b2 — "Phương án (tốn AI)" ở từng vấn đề. Hiện nút khi có MỘT trong hai prop; có `askOptionsDisabledReason` ⇒ nút tắt (Tooltip lý do). Không prop nào ⇒ không nút. */
  onAskOptions?: (issue: GateIssueView) => void;
  askOptionsDisabledReason?: string;
}

const STEP_TITLE: Record<GateStepKey, string> = { localize: 'Cổng AI — Bản địa hoá', episode_split: 'Cổng AI — Chia tập' };
const BORDER: Record<GateColor, string> = { default: 'var(--wb-btn-line)', success: 'var(--wb-ok)', error: 'var(--wb-bad)', warning: 'var(--wb-warn)', info: 'var(--wb-text-2)' };
const TAG_COLOR: Record<GateColor, string> = { default: 'default', success: 'success', error: 'error', warning: 'warning', info: 'processing' };

const IssueRow: React.FC<{ issue: GateIssueView; muted?: boolean; onAskOptions?: (issue: GateIssueView) => void; askOptionsDisabledReason?: string }> = ({ issue, muted, onAskOptions, askOptionsDisabledReason }) => (
  <div style={{ marginBottom: 10 }}>
    <Space size={6} wrap>
      <Tag color={muted ? 'default' : 'red'} style={{ margin: 0 }}>{issue.dimensionLabel}</Tag>
      <Text strong>{issue.where}</Text>
      <Text>{issue.problem}</Text>
      {(onAskOptions || askOptionsDisabledReason) && (
        <OptionsButton disabledReason={askOptionsDisabledReason} onClick={askOptionsDisabledReason || !onAskOptions ? undefined : () => onAskOptions(issue)} />
      )}
    </Space>
    {issue.quote && (
      <div style={{ marginTop: 4, paddingLeft: 10, borderLeft: '3px solid var(--wb-btn-line)' }}>
        <Text type="secondary" italic style={{ fontSize: 13 }}>“{issue.quote}”</Text>
      </div>
    )}
    {issue.suggestedFix && <div style={{ marginTop: 2 }}><Text type="secondary" style={{ fontSize: 13 }}>💡 {issue.suggestedFix}</Text></div>}
  </div>
);

const AdaptationGateCard: React.FC<Props> = ({ storyId, stepKey, gates, hasResult, onRerunStep, rerunAlreadyConfirmed, disabled, adoptJobId, onChanged, onAskOptions, askOptionsDisabledReason }) => {
  const { jobId: rejudgeJobId, setJobId: setRejudgeJobId, release: releaseRejudge } = useAdoptedJobId(storyId, adoptJobId ?? null);
  const [busy, setBusy] = useState<string | null>(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [reason, setReason] = useState('');

  const gate = gates?.[stepKey] ?? null;
  const view = useMemo(
    () => gateView(gate, { stepKey, hasResult, spentUsd: gates ? gateSpentUsd(gates) : null }),
    [gate, stepKey, hasResult, gates],
  );

  useFilmJobPoll(rejudgeJobId, {
    onTerminal: async (outcome, task) => {
      releaseRejudge();
      if (outcome === 'completed') message.success('Đã chấm lại.');
      else if (outcome === 'failed') message.error(task?.detail?.error || 'Chấm lại thất bại — xem log.', 8);
      else message.warning('Đã dừng theo dõi — bấm Tải lại.');
      onChanged();
    },
  });

  if (!view) return null;
  const locked = !!disabled || !!busy || !!rejudgeJobId;

  const rejudge = async () => {
    setBusy('rejudge');
    try {
      const r = await adaptationApi.rejudgeGate(storyId, stepKey);
      if (!r?.jobId) { message.warning('Server không trả job id.'); return; }
      setRejudgeJobId(r.jobId);
      message.info('Đang chấm lại bằng AI (nền)', 5);
    } catch (e) {
      const d = describeAdaptationError(e);
      message.error(`${d.title}${d.lines[0] ? ` — ${d.lines[0]}` : ''}`, 8);
    } finally {
      setBusy(null);
    }
  };

  const override = async () => {
    if (!isOverrideReasonValid(reason)) return;
    setBusy('override');
    try {
      await adaptationApi.overrideGate(storyId, stepKey, reason.trim());
      message.success('Đã vượt cổng — lý do được ghi vào kiểm toán.', 5);
      setOverrideOpen(false);
      setReason('');
      onChanged();
    } catch (e) {
      const d = describeAdaptationError(e);
      message.error(`${d.title}${d.lines[0] ? ` — ${d.lines[0]}` : ''}`, 8);
    } finally {
      setBusy(null);
    }
  };

  const chainLine = chainResultLine(gate);
  const rounds = roundRows(gate);

  return (
    <>
      <Card
        size="small" type="inner" style={{ marginBottom: 12, borderColor: BORDER[view.color] }}
        title={<Space size={8} wrap><Text strong>{STEP_TITLE[stepKey]}</Text><Tag color={TAG_COLOR[view.color]} style={{ margin: 0 }}>{view.label}</Tag></Space>}
        extra={gate?.model ? <Text type="secondary" style={{ fontSize: 12 }}>{gate.model}</Text> : undefined}
      >
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Text>{view.description}</Text>

          {view.blocking.map((i) => <IssueRow key={i.id} issue={i} onAskOptions={onAskOptions} askOptionsDisabledReason={askOptionsDisabledReason} />)}

          {view.advisory.length > 0 && (
            <Collapse
              size="small" ghost
              items={[{
                key: 'advisory',
                label: <Text type="secondary" style={{ fontSize: 13 }}>Gợi ý tham khảo ({view.advisory.length}) — không chặn</Text>,
                children: view.advisory.map((i) => <IssueRow key={i.id} issue={i} muted onAskOptions={onAskOptions} askOptionsDisabledReason={askOptionsDisabledReason} />),
              }]}
            />
          )}

          {view.overrideInfo && <Alert type="info" showIcon message={`Vượt cổng bởi ${view.overrideInfo}`} />}

          {(chainLine || rounds.length > 0) && (
            <Collapse
              size="small" ghost
              items={[{
                key: 'rounds',
                label: <Text type="secondary" style={{ fontSize: 13 }}>{chainLine ?? `Máy đã sửa ${rounds.length} vòng`}</Text>,
                children: rounds.map((r) => (
                  <div key={r.round} style={{ fontSize: 13 }}>
                    <Text strong>Vòng {r.round}</Text> · lỗi {r.change} · {r.fixed}
                    {r.skipped.length > 0 && <div><Text type="secondary">Bỏ qua: {r.skipped.join('; ')}</Text></div>}
                    {r.note && <div><Text type="warning">{r.note}</Text></div>}
                  </div>
                )),
              }]}
            />
          )}
          {view.notes.map((n) => <Text key={n} type="secondary" style={{ fontSize: 13 }}>ℹ {n}</Text>)}

          {rejudgeJobId && <Alert type="info" showIcon message="Đang chấm lại bằng AI ở nền" description={<span>Các nút bị khoá tới khi xong. <Tooltip title={`Mã kỹ thuật: ${rejudgeJobId}`}><Text type="secondary">Chi tiết kỹ thuật</Text></Tooltip></span>} />}

          <Space wrap>
            {view.canRerunStep && (
              rerunAlreadyConfirmed ? (
                <Button type="primary" danger disabled={locked} onClick={onRerunStep}>Sửa lại</Button>
              ) : (
                <Popconfirm
                  title="Sửa lại bước này?"
                  description={`Chạy nền, tốn AI — ${view.rerunHint}`}
                  okText="Sửa lại" cancelText="Thôi" disabled={locked} onConfirm={onRerunStep}
                >
                  <Button type="primary" danger disabled={locked}>Sửa lại</Button>
                </Popconfirm>
              )
            )}
            {view.canRejudge && (
              <Popconfirm title={`${view.state === 'STALE' ? 'Chấm lại sau localize' : 'Chấm lại'}?`} description="Chạy nền, tốn AI — chỉ chấm trạng thái hiện tại, không chạy lại bước." okText="Chấm lại" cancelText="Thôi" onConfirm={rejudge} disabled={locked}>
                <Button loading={busy === 'rejudge' || !!rejudgeJobId} disabled={locked}>
                  {view.state === 'STALE' ? 'Chấm lại sau localize' : 'Chấm lại'}
                </Button>
              </Popconfirm>
            )}
            {view.canOverride && (
              <Button disabled={locked} onClick={() => { setReason(''); setOverrideOpen(true); }}>Vẫn tiếp tục</Button>
            )}
            {view.canRerunStep && <Text type="secondary" style={{ fontSize: 13 }}>{view.rerunHint}</Text>}
          </Space>
        </Space>
      </Card>

      <Modal
        open={overrideOpen} title="Vượt cổng AI — có kiểm toán" okText="Xác nhận vượt cổng" cancelText="Huỷ"
        okButtonProps={{ disabled: !isOverrideReasonValid(reason), danger: true, loading: busy === 'override' }}
        onOk={override} onCancel={() => setOverrideOpen(false)}
      >
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Text>
            Bạn đang bỏ qua <Text strong>{view.blocking.length} lỗi khách quan</Text> để bước kế tiếp chạy được. Người thực hiện, thời gian,
            lý do và danh sách lỗi bỏ qua sẽ được ghi vào truyện.
          </Text>
          <Text strong>Lý do vượt cổng (bắt buộc, tối thiểu {OVERRIDE_REASON_MIN} ký tự)</Text>
          <TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="vd: đã đọc tay, đoạn này tác giả chấp thuận" maxLength={500} />
        </Space>
      </Modal>
    </>
  );
};

export default AdaptationGateCard;
